import React, { useState } from 'react';

const EVENT_META = {
  'tab-switch': { label: 'Přepnutí záložky', icon: '🔀', color: 'text-orange-700', bg: 'bg-orange-50', score: (e) => 25 + (e.duration > 10000 ? 15 : 0) },
  'app-blur': { label: 'Přepnutí aplikace', icon: '🖥️', color: 'text-red-600', bg: 'bg-red-50', score: (e) => 15 + (e.duration > 10000 ? 10 : 0) },
  'mouse-leave': { label: 'Kurzor opustil okno', icon: '🖱️', color: 'text-yellow-700', bg: 'bg-yellow-50', score: (e) => (e.duration > 5000 ? 5 : 0) },
  'paste': { label: 'Vložení obsahu (Ctrl+V)', icon: '📋', color: 'text-red-700', bg: 'bg-red-100', score: () => 25 },
  'copy': { label: 'Kopírování (Ctrl+C)', icon: '📄', color: 'text-gray-600', bg: 'bg-gray-50', score: () => 0 },
  'keyboard-shortcut': { label: 'Klávesová zkratka', icon: '⌨️', color: 'text-blue-600', bg: 'bg-blue-50', score: (e) => (e.detail === 'Ctrl+V' ? 15 : 0) },
  'devtools-detected': { label: 'Vývojářské nástroje otevřeny', icon: '🔧', color: 'text-red-800', bg: 'bg-red-100', score: () => 40 },
};

const formatDuration = (ms) => {
  if (!ms) return null;
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
};

const formatTime = (ts) => {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('cs', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const AntiCheatBadge = ({ antiCheatData }) => {
  if (!antiCheatData) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-400 border border-gray-200">
        — bez dat
      </span>
    );
  }
  const { verdict, score } = antiCheatData;
  const isSuspicious = verdict === 'suspicious';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold border ${
      isSuspicious
        ? 'bg-red-100 text-red-700 border-red-300'
        : 'bg-green-100 text-green-700 border-green-300'
    }`}>
      {isSuspicious ? '⚠️ Podezřelé' : '✅ Poctivý'}
      <span className="font-normal opacity-70">({score}b)</span>
    </span>
  );
};

const AntiCheatReport = ({ antiCheatData, studentName }) => {
  const [expanded, setExpanded] = useState(false);

  if (!antiCheatData) {
    return (
      <div className="text-xs text-gray-400 italic">Žádná anti-cheat data</div>
    );
  }

  const { events, score, verdict } = antiCheatData;
  const isSuspicious = verdict === 'suspicious';
  const filteredEvents = events.filter(e => {
    if (e.type === 'mouse-leave' && (!e.duration || e.duration <= 1000)) return false;
    return true;
  });

  return (
    <div className={`rounded-xl border-2 p-3 text-xs ${isSuspicious ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300'}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-base">{isSuspicious ? '⚠️' : '✅'}</span>
          <span className={`font-bold text-sm ${isSuspicious ? 'text-red-700' : 'text-green-700'}`}>
            {isSuspicious ? 'Pravděpodobně podvod' : 'Poctivý'}
          </span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${isSuspicious ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800'}`}>
            Skóre rizika: {score}
          </span>
          {studentName && (
            <span className="text-gray-500">{studentName}</span>
          )}
        </div>
        {filteredEvents.length > 0 && (
          <button
            onClick={() => setExpanded(v => !v)}
            className="flex-shrink-0 text-xs underline text-gray-500 hover:text-gray-700"
          >
            {expanded ? '▲ Skrýt' : `▼ ${filteredEvents.length} ${filteredEvents.length === 1 ? 'událost' : filteredEvents.length < 5 ? 'události' : 'událostí'}`}
          </button>
        )}
        {filteredEvents.length === 0 && (
          <span className="text-xs text-gray-400 italic">žádné podezřelé události</span>
        )}
      </div>

      {expanded && filteredEvents.length > 0 && (
        <div className="mt-3 space-y-1.5 border-t border-gray-200 pt-2">
          {filteredEvents.map((e, i) => {
            const meta = EVENT_META[e.type] || { label: e.type, icon: '❓', color: 'text-gray-600', bg: 'bg-gray-50', score: () => 0 };
            const eventScore = meta.score(e);
            const dur = formatDuration(e.duration);
            return (
              <div key={i} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${meta.bg}`}>
                <span className="text-sm">{meta.icon}</span>
                <span className={`font-semibold ${meta.color} flex-1`}>{meta.label}</span>
                {e.detail && (
                  <span className="text-gray-500">
                    {e.type === 'paste' ? `${e.detail} znaků` : e.detail}
                  </span>
                )}
                {dur && (
                  <span className="text-gray-500 font-mono">{dur}</span>
                )}
                <span className="text-gray-400 font-mono text-xs">{formatTime(e.timestamp)}</span>
                {eventScore > 0 && (
                  <span className="text-red-600 font-bold text-xs">+{eventScore}b</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export { AntiCheatBadge };
export default AntiCheatReport;
